import { dayKey, hoursToMinutes } from '@app/utils';
import { PendingAbsenceEntity, ReportEntity, WorkDayUserEntity } from '@data';
import { useGTMEvents } from '@data/events';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { fullDate } from '../../app/utils/helpers/date-utils';

import * as api from '../api';
import * as queryKeys from '../constants/keys';
import { invalidatePmProductLists } from './pm-product-lists';
import {
  DailyReportDefaults,
  IError,
  ListResponse,
  OrganizationEntity,
  ProductStatus,
  SprintProductReportsParams,
} from '../utils/types';
import { useListProducts } from './products';
import { reportsInfiniteNextPageParam } from './reports-pagination';

type QueryContext = { previousReports: ReportEntity[] };
type UpdatedReportPmQueryContext = { previousReports?: ListResponse<ReportEntity> };
type CreateReportPmQueryContext = { previousReports?: ListResponse<ReportEntity>; newReport: Partial<ReportEntity> };

type ReportInfinitePage = {
  from: Date | string;
  to: Date | string;
  workDaysData: WorkDayUserEntity[];
};

type ReportsInfiniteData = InfiniteData<ReportInfinitePage, { from?: Date; to?: Date } | undefined>;

export const useListReports = (
  organizationId: string | undefined,
  userId: string,
  params: { from?: string | Date; to?: string | Date; productIds?: string[]; offset?: number; limit?: number },
  isEnabled: boolean,
  hideWeekend: boolean,
) =>
  useInfiniteQuery({
    queryKey: [queryKeys.REPORTS, { organizationId, userId, params, hideWeekend }],
    queryFn: ({ pageParam }) => api.fetchReports(organizationId!, userId, params, hideWeekend, { pageParam }),
    initialPageParam: undefined as { from?: Date; to?: Date } | undefined,
    enabled: isEnabled && !!organizationId,
    placeholderData: keepPreviousData,
    getNextPageParam: reportsInfiniteNextPageParam,
  });

/**
 * One person's reports for a single window, flat rather than paged by sprint: the per-product hours card
 * on their page sums them, so a partial answer would read as time nobody filed.
 */
export const useUserReportsInRange = (
  organizationId: string | undefined,
  userId: string | undefined,
  params: { from: string | Date; to: string | Date } | undefined,
  enabled = true,
): UseQueryResult<ReportEntity[], IError> =>
  useQuery({
    queryKey: [queryKeys.USER_REPORTS_IN_RANGE, { organizationId, userId, params }],
    queryFn: () => api.fetchUserReportsInRange(organizationId!, userId!, params!),
    enabled: enabled && !!organizationId && !!userId && !!params,
  });

/**
 * How many weekdays in the window the user still owes. The window is the caller's, so this and the To
 * report list are the same thirty days — see the epic's architecture doc, Decision 2.
 */
export const useUnreportedDayCount = (
  organizationId: string | undefined,
  userId: string | undefined,
  params: { from: Date | string; to: Date | string } | undefined,
  enabled = true,
): UseQueryResult<{ count: number; days: string[] }, IError> =>
  useQuery({
    queryKey: [queryKeys.UNREPORTED_DAYS, { organizationId, userId, params }],
    queryFn: () => api.fetchUnreportedDayCount(organizationId!, userId!, params!),
    enabled: enabled && !!organizationId && !!userId && !!params,
  });

/** The days this member has called finished, so the To report list can drop them. */
export const useFinishedDays = (
  organizationId: string | undefined,
  userId: string | undefined,
  params: { from: Date | string; to: Date | string } | undefined,
  enabled = true,
): UseQueryResult<{ days: string[] }, IError> =>
  useQuery({
    queryKey: [queryKeys.FINISHED_DAYS, { organizationId, userId, params }],
    queryFn: () => api.fetchFinishedDays(organizationId!, userId!, params!),
    enabled: enabled && !!organizationId && !!userId && !!params,
    // As the reports beside it: without this, paging a year reads every settled day as owed mid-fetch, and
    // the dots come back while the marked rows animate out.
    placeholderData: keepPreviousData,
  });

// Only today ever has any: the evening run files it and clears it.
export const useTodayPendingAbsences = (
  organizationId: string | undefined,
  userId: string | undefined,
  todayDate: Date,
): PendingAbsenceEntity[] => {
  const today = dayKey(todayDate);
  const params = { from: `${today}T00:00:00.000Z`, to: `${today}T23:59:59.999Z` };
  const { data } = useQuery({
    queryKey: [queryKeys.PENDING_ABSENCES, { organizationId, userId, today }],
    queryFn: () => api.fetchPendingAbsences(organizationId!, userId!, params),
    enabled: !!organizationId && !!userId,
  });

  return data?.absences ?? [];
};

/**
 * Marks a day finished, or takes the mark off again.
 *
 * Both directions in one mutation: the button is a toggle, and splitting it would make the caller hold which
 * way it is pointing on top of the state it already reads.
 */
export const useSetDayFinished = (
  organizationId: string | undefined,
  userId: string | undefined,
): UseMutationResult<void, IError, { workday: string; finished: boolean }, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workday, finished }) =>
      finished
        ? api.markDayFinished(organizationId!, userId!, workday)
        : api.markDayUnfinished(organizationId!, userId!, workday),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.FINISHED_DAYS] });
      // The nav badge counts the same days, so it has to move with the row.
      queryClient.invalidateQueries({ queryKey: [queryKeys.UNREPORTED_DAYS] });
    },
  });
};

/** What the Phill report form opens with. Cheap, and only asked for while the dialog is open. */
export const useDailyReportDefaults = (
  organizationId?: string,
  enabled = true,
): UseQueryResult<DailyReportDefaults, IError> =>
  useQuery({
    queryKey: [queryKeys.DAILY_REPORT_DEFAULTS, organizationId],
    queryFn: () => api.fetchDailyReportDefaults(organizationId!),
    enabled: enabled && !!organizationId,
    staleTime: 60 * 1000,
  });

/**
 * The AI draft for one day.
 *
 * A mutation rather than a query: it starts real work every time it is called — GitHub, the calendar, Slack
 * and a model — and it is asked for at a moment the person chose, not whenever a cache expires.
 */
export const useDailyReportDraft = (): UseMutationResult<
  { drafts: Record<string, string[]> },
  IError,
  { organizationId: string; productNames: string[]; workday: Date },
  unknown
> =>
  useMutation({
    mutationFn: ({ organizationId, productNames, workday }) =>
      api.draftDailyReport(organizationId, { productNames, workday }),
  });

export const useCreateReport = (
  userId: string,
  organizationId: string,
  params: { from?: string | Date; to?: string | Date; productIds?: string[]; offset?: number; limit?: number },
  hideWeekend: boolean,
): UseMutationResult<ReportEntity, IError, Partial<ReportEntity>, QueryContext> => {
  const queryClient = useQueryClient();
  const { data: products } = useListProducts(organizationId, {
    statuses: [ProductStatus.active, ProductStatus.support],
  });

  const { events, trackEvent } = useGTMEvents();

  const reportQueryKey = [queryKeys.REPORTS, { organizationId, userId, params, hideWeekend }] as const;

  return useMutation({
    mutationFn: (values: Partial<ReportEntity>) => api.createReport(values),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: reportQueryKey });
      queryClient.invalidateQueries({ queryKey: [queryKeys.UNREPORTED_DAYS] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCTS] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ROLES] });
      // GTM track event
      trackEvent(events.ReportSubmit, { teamName: data.involvement.role, today: data.today });
    },
    onMutate: async (newReport: Partial<ReportEntity> & { hours?: number; productId?: string }) => {
      await queryClient.cancelQueries({ queryKey: reportQueryKey });

      const previousReports = queryClient.getQueryData(reportQueryKey) as ReportEntity[];

      queryClient.setQueryData(reportQueryKey, (old: ReportsInfiniteData | undefined) => {
        if (!old) {
          return old;
        }
        const newItem = {
          id: uuidv4(),
          minutes: (newReport.hours ?? 0) * 60,
          involvement: {
            id: uuidv4(),
            product: products?.items?.find(product => product.id === newReport.productId),
          },
          ...newReport,
        } as ReportEntity;

        return {
          ...old,
          pages: old.pages.reduce((newPages, page) => {
            const newPage = {
              ...page,
              workDaysData: page.workDaysData.reduce(
                (newList, dayReports) => [
                  ...newList,
                  {
                    ...dayReports,
                    reports:
                      fullDate(newItem.workday) === dayReports.date
                        ? [...dayReports.reports, newItem]
                        : dayReports.reports,
                  },
                ],
                [],
              ),
            };
            return [...newPages, newPage];
          }, []),
        };
      });

      return { previousReports };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(reportQueryKey, context?.previousReports);
    },
  });
};

/** The views read the chapter off `involvement.role`, while the form sends it as a bare `role`. */
const movedTo = (report: ReportEntity, values: Partial<ReportEntity>) =>
  values.role ? { ...report.involvement, role: values.role } : report.involvement;

export const useUpdateReport = (
  organizationId: string,
  userId: string,
  params: { from?: string | Date; to?: string | Date; productIds?: string[]; offset?: number; limit?: number },
  hideWeekend: boolean,
): UseMutationResult<ReportEntity, IError, Partial<ReportEntity>, QueryContext> => {
  const queryClient = useQueryClient();
  const reportQueryKey = [queryKeys.REPORTS, { organizationId, userId, params, hideWeekend }] as const;

  return useMutation({
    mutationFn: (values: Partial<ReportEntity>) => api.updateReport(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportQueryKey });
      queryClient.invalidateQueries({ queryKey: [queryKeys.UNREPORTED_DAYS] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ROLES] });
    },
    onMutate: async (newReport: Partial<ReportEntity>) => {
      await queryClient.cancelQueries({ queryKey: reportQueryKey });

      const previousReports = queryClient.getQueryData(reportQueryKey) as ReportEntity[];

      queryClient.setQueryData(reportQueryKey, (old: ReportsInfiniteData | undefined) => {
        if (!old) {
          return old;
        }
        const newData = old.pages.reduce((newPages, page) => {
          const newPage = {
            ...page,
            workDaysData: page.workDaysData.reduce(
              (newList, dayReports) => [
                ...newList,
                {
                  ...dayReports,
                  reports:
                    newReport.workday != null && fullDate(newReport.workday) === dayReports.date
                      ? dayReports.reports.map(report =>
                          report.id === newReport.id
                            ? { ...report, ...newReport, involvement: movedTo(report, newReport) }
                            : report,
                        )
                      : dayReports.reports,
                },
              ],
              [],
            ),
          };
          return [...newPages, newPage];
        }, []);

        return { ...old, pages: newData };
      });

      return { previousReports };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(reportQueryKey, context?.previousReports);
    },
  });
};

export const useRemoveReport = (
  organizationId: string,
  userId: string,
  params: { from?: string | Date; to?: string | Date; productIds?: string[]; offset?: number; limit?: number },
  hideWeekend: boolean,
): UseMutationResult<void, IError, string, QueryContext> => {
  const queryClient = useQueryClient();
  const reportQueryKey = [queryKeys.REPORTS, { organizationId, userId, params, hideWeekend }] as const;

  return useMutation({
    mutationFn: (reportId: string) => api.removeReport(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportQueryKey });
      queryClient.invalidateQueries({ queryKey: [queryKeys.UNREPORTED_DAYS] });
      // Taking time off a report changes the hours the products reporting list shows, and that list now
      // reads a cache entry per sprint as well as the plain PM list.
      invalidatePmProductLists(queryClient);
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: reportQueryKey });

      const previousReports = queryClient.getQueryData(reportQueryKey) as ReportEntity[];

      queryClient.setQueryData(reportQueryKey, (old: ReportsInfiniteData | undefined) => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          pages: old.pages.reduce((newPages, page) => {
            const newPage = {
              ...page,
              workDaysData: page.workDaysData.reduce(
                (newList, dayReports) => [
                  ...newList,
                  { ...dayReports, reports: dayReports.reports.filter(report => String(report.id) !== String(id)) },
                ],
                [],
              ),
            };
            return [...newPages, newPage];
          }, []),
        };
      });
      return { previousReports };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(reportQueryKey, context?.previousReports);
    },
  });
};

export const useCreatePmReport = (
  organizationId: string,
  params: SprintProductReportsParams,
): UseMutationResult<ReportEntity, IError, Partial<ReportEntity>, CreateReportPmQueryContext> => {
  const queryClient = useQueryClient();
  const pmKey = [queryKeys.PM_SPRINT_PRODUCT_REPORTS, params] as const;

  return useMutation({
    mutationFn: (values: Partial<ReportEntity>) => api.createReport(values),
    onMutate: async values => {
      const previousReports = queryClient.getQueryData<ListResponse<ReportEntity>>(pmKey);

      const member = queryClient
        .getQueryData<OrganizationEntity>([queryKeys.ORGANIZATION, organizationId])
        ?.memberships.find(m => m.user.id === values.userId);

      const newReport = {
        ...values,
        id: uuidv4(),
        minutes: hoursToMinutes(values.hours),
        involvement: { role: values.role, membership: member },
      };

      queryClient.setQueryData(pmKey, (old: ListResponse<ReportEntity>) => ({
        ...old,
        items: [...old.items, newReport],
      }));

      return { previousReports, newReport: newReport as Partial<ReportEntity> };
    },
    onSuccess: (data, _, context) => {
      queryClient.setQueryData(pmKey, (old: ListResponse<ReportEntity>) => ({
        ...old,
        items: old.items.map(x => (x.id === context?.newReport.id ? { ...x, id: data.id } : x)),
      }));
      queryClient.invalidateQueries({ queryKey: [queryKeys.ROLES] });
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(pmKey, context?.previousReports);
    },
  });
};

export const useUpdatePmReport = (
  params: SprintProductReportsParams,
): UseMutationResult<ReportEntity, IError, Partial<ReportEntity>, UpdatedReportPmQueryContext> => {
  const queryClient = useQueryClient();
  const pmKey = [queryKeys.PM_SPRINT_PRODUCT_REPORTS, params] as const;

  return useMutation({
    mutationFn: (values: Partial<ReportEntity>) => api.updateReport(values),
    onMutate: async (updatedReport: Partial<ReportEntity>) => {
      const previousReports = queryClient.getQueryData<ListResponse<ReportEntity>>(pmKey);

      queryClient.setQueryData(pmKey, (old: ListResponse<ReportEntity>) => ({
        ...old,
        items: old.items.map(x =>
          x.id === updatedReport.id
            ? {
                ...x,
                ...updatedReport,
                minutes: hoursToMinutes(updatedReport.hours),
                involvement: movedTo(x, updatedReport),
              }
            : x,
        ),
      }));

      return { previousReports };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.ROLES] });
      // The server owns which involvement the entry landed on; the move above only holds the screen still.
      queryClient.invalidateQueries({ queryKey: pmKey });
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(pmKey, context?.previousReports);
    },
  });
};

export const useRemovePmReport = (
  params: SprintProductReportsParams,
): UseMutationResult<void, IError, string, UpdatedReportPmQueryContext> => {
  const queryClient = useQueryClient();
  const pmKey = [queryKeys.PM_SPRINT_PRODUCT_REPORTS, params] as const;

  return useMutation({
    mutationFn: (reportId: string) => api.removeReport(reportId),
    onMutate: async (reportId: string) => {
      const previousReports = queryClient.getQueryData<ListResponse<ReportEntity>>(pmKey);
      queryClient.setQueryData(pmKey, (old: ListResponse<ReportEntity>) => ({
        ...old,
        items: old.items.filter(i => i.id !== reportId),
      }));
      return { previousReports };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(pmKey, context?.previousReports);
    },
  });
};

export const useReportSummary = (): UseMutationResult<
  string,
  IError,
  {
    productId: string;
    since: string;
    until: string;
  },
  unknown
> =>
  useMutation({
    mutationFn: ({ productId, since, until }) => api.fetchSummary({ productId, since, until }),
  });
